/* Step components for the revised MarketOS onboarding flow. */
import React, { useState, useEffect, useRef } from 'react';
import { Icons as Ic } from './icons.jsx';

/* ---------- shared footer nav ---------- */
function StepFooter({ onBack, backHidden, onSkip, skipLabel = "Skip", onNext, nextLabel = "Continue", nextDisabled, nextIcon = true }) {
  return (
    <div className="step-footer">
      {backHidden ? <span /> : (
        <button className="btn-ghost" onClick={onBack}>
          <Ic.ArrowLeft /> Back
        </button>
      )}
      <div className="right">
        {onSkip && (
          <button className="btn-ghost" onClick={onSkip}>{skipLabel}</button>
        )}
        <button className="btn-primary font-semibold" onClick={onNext} disabled={nextDisabled}>
          {nextLabel}
          {nextIcon && <Ic.ArrowRight />}
        </button>
      </div>
    </div>
  );
}

/* ---------- 1. Seller type ---------- */
function SellerTypeStep({ state, set, onNext, onBack, onSkip, backHidden }) {
  const choose = (v) => set({ sellerType: v });
  const options = [
    {
      id: "existing",
      icon: <Ic.TrendingUp />,
      title: "I already sell on Amazon",
      desc: "You have an active Seller Central account with live listings and sales history.",
    },
    {
      id: "new",
      icon: <Ic.Rocket />,
      title: "I'm new to Amazon",
      desc: "You're just getting started and don't have existing Amazon sales yet.",
    },
  ];
  return (
    <div className="card">
      <div className="card-body">
        <div className="step-icon"><Ic.Store /></div>
        <h2 className="card-title text-center">Tell us about your Amazon business</h2>
        <p className="description text-center">
          This helps us tailor your setup. You can pull existing listings, or start a fresh catalog.
        </p>

        <div className="seller-grid">
          {options.map((o) => (
            <button
              key={o.id}
              className={`seller-card ${state.sellerType === o.id ? "selected" : ""}`}
              onClick={() => choose(o.id)}
            >
              <span className="sc-check"><Ic.Check /></span>
              <span className="sc-icon">{o.icon}</span>
              <h3>{o.title}</h3>
              <p>{o.desc}</p>
            </button>
          ))}
        </div>

        <StepFooter
          onBack={onBack}
          backHidden={backHidden}
          onSkip={onSkip}
          onNext={onNext}
          nextDisabled={!state.sellerType}
        />
      </div>
    </div>
  );
}

/* ---------- 2. Connect Amazon ---------- */
function ConnectAmazonStep({ state, set, onNext, onBack, onSkip }) {
  const [connecting, setConnecting] = useState(false);
  const connected = state.connected;
  const isNew = state.sellerType === "new";

  const connect = () => {
    setConnecting(true);
    setTimeout(() => {
      setConnecting(false);
      set({ connected: true });
    }, 1400);
  };
  const disconnect = () => set({ connected: false });

  return (
    <div className="card">
      <div className="card-body">
        <div className="step-icon"><Ic.Link /></div>
        <h2 className="card-title text-center">Connect your Amazon Seller account</h2>
        <p className="description text-center">
          {isNew
            ? "Connecting is optional while you're getting started — link it whenever your Seller Central account is ready."
            : "Securely connect Seller Central so we can pull your SKUs, listings, and order history."}
        </p>

        <div className="account-card" style={{ padding: "24px" }}>
          <div className="account-header" style={{ marginBottom: "18px" }}>
            <div className="account-info">
              <div className="account-icon amazon"><span className="account-icon-text">a</span></div>
              <div className="account-details">
                <h3 className="mb-1">Amazon Seller Central</h3>
                <p className="font-medium">Marketplace: United States</p>
              </div>
            </div>
            <div className={`status ${connected ? "connected" : "not-connected"}`}>
              {connected ? "Connected" : "Not Connected"}
            </div>
          </div>

          <div className="info-box" style={{ marginBottom: connected ? "18px" : "0" }}>
            <div className="info-content">
              <Ic.Info size={18} style={{ color: "var(--onboarding-primary)", marginRight: "8px", marginTop: "1px", flexShrink: 0 }} />
              <div className="info-text">
                <p>What we'll sync:</p>
                <ul>
                  <li>SKUs, ASINs and product listings</li>
                  <li>Inventory levels and order history</li>
                  <li>Sales analytics and performance</li>
                </ul>
              </div>
            </div>
          </div>

          {connected ? (
            <div className="actions-column">
              <div className="success">
                <span className="success-icon"><Ic.CircleCheck size={18} /></span>
                <span className="success-text">Successfully connected</span>
              </div>
              <button className="btn-outline" onClick={disconnect}>
                <Ic.X /> Disconnect account
              </button>
            </div>
          ) : (
            <button className="btn-primary w-100 font-semibold" onClick={connect} disabled={connecting}>
              {connecting ? <Ic.RefreshCw style={{ animation: "spin-slow 1s linear infinite" }} /> : <Ic.ExternalLink />}
              {connecting ? "Connecting…" : "Connect Amazon account"}
            </button>
          )}
        </div>

        <StepFooter
          onBack={onBack}
          onSkip={onSkip}
          skipLabel="I'll connect later"
          onNext={onNext}
          nextDisabled={!connected}
        />
      </div>
    </div>
  );
}

/* ---------- 3. Pull SKUs ---------- */
function PullSkusStep({ state, set, onNext, onBack, onSkip }) {
  const isNew = state.sellerType === "new";
  const [phase, setPhase] = useState(state.skus ? "done" : "idle"); // idle | pulling | done
  const [pct, setPct] = useState(state.skus ? 100 : 0);

  useEffect(() => {
    if (phase !== "pulling") return;
    let p = 0;
    const id = setInterval(() => {
      p = Math.min(100, p + Math.random() * 16 + 6);
      setPct(Math.round(p));
      if (p >= 100) {
        clearInterval(id);
        setTimeout(() => {
          set({ skus: { total: 412, active: 388, inactive: 24 } });
          setPhase("done");
        }, 450);
      }
    }, 320);
    return () => clearInterval(id);
  }, [phase]);

  /* New-to-Amazon: nothing to pull */
  if (isNew) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="step-icon"><Ic.Sparkles /></div>
          <h2 className="card-title text-center">You're starting with a clean slate</h2>
          <p className="description text-center">
            Since you don't have Amazon sales yet, there are no existing SKUs to import. In the next
            steps you'll build your catalog and warehouse inventory from a single upload.
          </p>
          <div className="info-box lined">
            <div className="info-row">
              <Ic.Info />
              <div>
                <div className="it-title">What happens next</div>
                <div className="it-body">
                  You'll upload one mapping file to create SKUs, products and vendor items, then add
                  your warehouse stock with quantities and cost.
                </div>
              </div>
            </div>
          </div>
          <StepFooter onBack={onBack} onNext={onNext} nextLabel="Set up my catalog" />
        </div>
      </div>
    );
  }

  /* Existing seller */
  return (
    <div className="card">
      <div className="card-body">
        <div className={`step-icon ${phase === "done" ? "success" : ""}`}>
          {phase === "done" ? <Ic.CircleCheck /> : <Ic.Boxes />}
        </div>
        <h2 className="card-title text-center">
          {phase === "done" ? "SKUs imported from Amazon" : "Pull your SKUs from Amazon"}
        </h2>
        <p className="description text-center">
          {phase === "done"
            ? "We pulled your live catalog from Seller Central. You'll map these to products and vendor items next."
            : "We'll import your active and inactive listings directly from your connected Seller Central account."}
        </p>

        {phase === "idle" && (
          <>
            {!state.connected && (
              <div className="info-box lined" style={{ marginBottom: "18px" }}>
                <div className="info-row">
                  <Ic.Info />
                  <div>
                    <div className="it-title">Amazon not connected</div>
                    <div className="it-body">Connect your account on the previous step to pull SKUs automatically, or skip and add them by upload.</div>
                  </div>
                </div>
              </div>
            )}
            <button
              className="btn-primary w-100 font-semibold"
              onClick={() => setPhase("pulling")}
              disabled={!state.connected}
              style={{ height: "44px" }}
            >
              <Ic.RefreshCw /> Pull SKUs from Amazon
            </button>
          </>
        )}

        {phase === "pulling" && (
          <div style={{ padding: "8px 0 4px" }}>
            <div className="pct-label">
              <span className="l">Importing listings…</span>
              <span className="r">{pct}%</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <p className="progress-text">Fetching SKUs, ASINs and listing status from Seller Central</p>
          </div>
        )}

        {phase === "done" && (
          <div className="pull-result">
            <div className="pull-tile"><div className="pt-num">412</div><div className="pt-lbl">SKUs imported</div></div>
            <div className="pull-tile"><div className="pt-num">388</div><div className="pt-lbl">Active</div></div>
            <div className="pull-tile"><div className="pt-num">24</div><div className="pt-lbl">Inactive</div></div>
          </div>
        )}

        <StepFooter
          onBack={onBack}
          onSkip={phase === "done" ? null : onSkip}
          skipLabel="Skip — I'll upload instead"
          onNext={onNext}
          nextDisabled={phase === "pulling"}
          nextLabel={phase === "done" ? "Continue" : "Continue"}
        />
      </div>
    </div>
  );
}

export { StepFooter, SellerTypeStep, ConnectAmazonStep, PullSkusStep };
